import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import '../css/BorstweringenInventarisatie.css';

const API_URL = '';

const translations = {
  nl: {
    title: 'Borstweringen Isolatie — Inventarisatie',
    subtitle: 'Heeft het werk bij u plaatsgevonden?',
    greeting: 'Beste bewoner,',
    intro: 'In april zijn de monteurs van Robertson langsgekomen om de borstweringen op de balkons te isoleren (de witte platen in het kozijn). <strong>Helaas is er door de monteurs niet bijgehouden welke appartementen zijn gedaan.</strong>',
    ask: 'Wij vragen u daarom kort aan te geven of het werk bij u is uitgevoerd, zodat we kunnen inventariseren waar nog werk nodig is. Het invullen duurt minder dan een minuut.',
    houseNumber: 'Huisnummer',
    houseNumberPlaceholder: 'Bijv. 42',
    name: 'Naam',
    namePlaceholder: 'Uw volledige naam',
    email: 'E-mailadres (optioneel)',
    emailPlaceholder: 'uw@email.nl',
    statusLabel: 'Wat is de status bij u?',
    statusDone: 'Helemaal gedaan',
    statusDoneHint: 'De borstweringen zijn volledig geïsoleerd',
    statusPartial: 'Gedeeltelijk gedaan',
    statusPartialHint: 'Bijvoorbeeld slechts één paneel of één balkon',
    statusNotDone: 'Niet gedaan',
    statusNotDoneHint: 'De monteurs zijn niet bij mij geweest',
    notes: 'Opmerkingen (optioneel)',
    notesPlaceholder: 'Bijv. welk paneel wel/niet, of een toelichting...',
    submit: 'Antwoord opslaan',
    submitting: 'Bezig met opslaan...',
    alreadyFilled: (date) => `U heeft eerder op ${date} ingevuld. U kunt uw antwoord hieronder aanpassen.`,
    successTitle: 'Bedankt!',
    successMessage: 'Uw antwoord is opgeslagen. U kunt deze pagina sluiten of een aanpassing maken en opnieuw opslaan.',
    footer: 'Vragen? Mail',
    errorStatus: 'Kies een status',
    errorRequired: 'Vul huisnummer en naam in',
    errorGeneral: 'Er is een fout opgetreden. Probeer het opnieuw.'
  },
  en: {
    title: 'Balcony Insulation — Inventory',
    subtitle: 'Has the work been completed at your apartment?',
    greeting: 'Dear resident,',
    intro: 'In April the Robertson team came to insulate the balcony parapets (the white panels in the window frame). <strong>Unfortunately the team did not record which apartments they completed.</strong>',
    ask: 'Could you please briefly let us know whether the work was carried out at your apartment, so we can determine where work is still needed. It takes less than a minute.',
    houseNumber: 'House number',
    houseNumberPlaceholder: 'E.g. 42',
    name: 'Name',
    namePlaceholder: 'Your full name',
    email: 'Email address (optional)',
    emailPlaceholder: 'your@email.com',
    statusLabel: 'What is the status at your apartment?',
    statusDone: 'Fully completed',
    statusDoneHint: 'The parapets have been fully insulated',
    statusPartial: 'Partially completed',
    statusPartialHint: 'For example only one panel or one balcony',
    statusNotDone: 'Not done',
    statusNotDoneHint: 'The team did not come by',
    notes: 'Notes (optional)',
    notesPlaceholder: 'E.g. which panel was/wasn’t done, or further explanation...',
    submit: 'Save answer',
    submitting: 'Saving...',
    alreadyFilled: (date) => `You previously responded on ${date}. You can update your answer below.`,
    successTitle: 'Thank you!',
    successMessage: 'Your answer has been saved. You can close this page or make a change and save again.',
    footer: 'Questions? Email',
    errorStatus: 'Please choose a status',
    errorRequired: 'Please fill in house number and name',
    errorGeneral: 'Something went wrong. Please try again.'
  }
};

const STATUS_OPTIONS = [
  { value: 'gedaan', icon: '✅' },
  { value: 'gedeeltelijk', icon: '🟡' },
  { value: 'niet', icon: '❌' }
];

const useQueryParam = (key) => {
  const { search } = useLocation();
  return new URLSearchParams(search).get(key);
};

const BorstweringenInventarisatie = () => {
  const initialHuisnummer = useQueryParam('h') || '';

  const [lang, setLang] = useState(localStorage.getItem('language') || 'nl');
  const [form, setForm] = useState({
    huisnummer: initialHuisnummer,
    naam: '',
    email: '',
    status: '',
    opmerkingen: ''
  });
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const t = translations[lang];

  useEffect(() => {
    localStorage.setItem('language', lang);
  }, [lang]);

  const checkExisting = useCallback(async (huisnummer) => {
    if (!huisnummer || !String(huisnummer).trim()) {
      setExisting(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/borstweringen-inventarisatie/check/${encodeURIComponent(huisnummer.trim())}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data && data._id) {
        setExisting(data);
        setForm(prev => ({
          ...prev,
          naam: prev.naam || data.naam || '',
          email: prev.email || data.email || '',
          status: prev.status || data.status || '',
          opmerkingen: prev.opmerkingen || data.opmerkingen || ''
        }));
      } else {
        setExisting(null);
      }
    } catch (err) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    if (initialHuisnummer) {
      checkExisting(initialHuisnummer);
    }
  }, [initialHuisnummer, checkExisting]);

  const handleHuisnummerBlur = () => {
    if (form.huisnummer) {
      checkExisting(form.huisnummer);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.huisnummer.trim() || !form.naam.trim()) {
      setError(t.errorRequired);
      return;
    }
    if (!form.status) {
      setError(t.errorStatus);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/borstweringen-inventarisatie/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || t.errorGeneral);
        return;
      }
      setSuccess(true);
      setExisting(data.response);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(t.errorGeneral);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const statusLabel = (value) => {
    if (value === 'gedaan') return t.statusDone;
    if (value === 'gedeeltelijk') return t.statusPartial;
    if (value === 'niet') return t.statusNotDone;
    return '';
  };

  return (
    <div className="inventarisatie-page">
      <div className="inventarisatie-container">
        <div className="lang-toggle">
          <button
            className={`lang-btn ${lang === 'nl' ? 'active' : ''}`}
            onClick={() => setLang('nl')}
            type="button"
          >NL</button>
          <button
            className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
            onClick={() => setLang('en')}
            type="button"
          >EN</button>
        </div>

        <div className="inventarisatie-header">
          <img src={LogoPolderzicht} alt="VVE Polderzicht" className="inventarisatie-logo" />
          <h1>{t.title}</h1>
          <p className="inventarisatie-subtitle">{t.subtitle}</p>
        </div>

        {success ? (
          <div className="inventarisatie-success">
            <div className="success-icon">✅</div>
            <h2>{t.successTitle}</h2>
            <p>{t.successMessage}</p>
            {existing && (
              <div className="success-summary">
                <div><strong>{t.houseNumber}:</strong> {existing.huisnummer}</div>
                <div><strong>{t.statusLabel.replace(/[?:]/g, '')}:</strong> {statusLabel(existing.status)}</div>
              </div>
            )}
            <button
              type="button"
              className="inventarisatie-submit secondary"
              onClick={() => setSuccess(false)}
            >
              {lang === 'nl' ? 'Antwoord aanpassen' : 'Edit answer'}
            </button>
          </div>
        ) : (
          <>
            <div className="inventarisatie-info">
              <p>{t.greeting}</p>
              <p dangerouslySetInnerHTML={{ __html: t.intro }} />
              <p>{t.ask}</p>
            </div>

            {existing && existing.submittedAt && (
              <div className="inventarisatie-alert info">
                {t.alreadyFilled(formatDate(existing.submittedAt))}
              </div>
            )}

            {error && (
              <div className="inventarisatie-alert error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="inventarisatie-form">
              <div className="form-group">
                <label htmlFor="huisnummer">{t.houseNumber} *</label>
                <input
                  type="text"
                  id="huisnummer"
                  value={form.huisnummer}
                  onChange={(e) => setForm({ ...form, huisnummer: e.target.value })}
                  onBlur={handleHuisnummerBlur}
                  placeholder={t.houseNumberPlaceholder}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="naam">{t.name} *</label>
                <input
                  type="text"
                  id="naam"
                  value={form.naam}
                  onChange={(e) => setForm({ ...form, naam: e.target.value })}
                  placeholder={t.namePlaceholder}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t.email}</label>
                <input
                  type="email"
                  id="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.emailPlaceholder}
                />
              </div>

              <div className="form-group">
                <label>{t.statusLabel} *</label>
                <div className="status-options">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`status-card status-${opt.value} ${form.status === opt.value ? 'selected' : ''}`}
                      onClick={() => setForm({ ...form, status: opt.value })}
                    >
                      <div className="status-icon">{opt.icon}</div>
                      <div className="status-label">{statusLabel(opt.value)}</div>
                      <div className="status-hint">
                        {opt.value === 'gedaan' && t.statusDoneHint}
                        {opt.value === 'gedeeltelijk' && t.statusPartialHint}
                        {opt.value === 'niet' && t.statusNotDoneHint}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="opmerkingen">{t.notes}</label>
                <textarea
                  id="opmerkingen"
                  value={form.opmerkingen}
                  onChange={(e) => setForm({ ...form, opmerkingen: e.target.value })}
                  placeholder={t.notesPlaceholder}
                  rows={4}
                  maxLength={2000}
                />
              </div>

              <button
                type="submit"
                className="inventarisatie-submit"
                disabled={submitting}
              >
                {submitting ? t.submitting : t.submit}
              </button>
            </form>
          </>
        )}

        <div className="inventarisatie-footer">
          <p>{t.footer} <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default BorstweringenInventarisatie;
